import {useInfiniteQuery, InfiniteData, useMutation, useQueryClient, UseMutationResult} from '@tanstack/react-query';
import {ConsentsDocumentsFilter, ConsentsDocumentsResponse, CreateConsentDocumentDTO} from '../model';
import {consentsApi} from '../api';
import {useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type ConsentsDocumentsState = {
  getInfiniteDocuments: ReturnType<
    typeof useInfiniteQuery<
      ConsentsDocumentsResponse,
      Error,
      InfiniteData<ConsentsDocumentsResponse>,
      ['consents-documents', 'infinite', Omit<ConsentsDocumentsFilter, 'after'>],
      string | undefined
    >
  >;
  filters: ConsentsDocumentsFilter;
  updateFilter: (newFilter: Partial<ConsentsDocumentsFilter>) => void;
  createDocumentMutation: UseMutationResult<void, Error, CreateConsentDocumentDTO>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useConsentsDocuments = (initialFilter: ConsentsDocumentsFilter = {}): ConsentsDocumentsState => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();
  const [filters, setFilters] = useState<ConsentsDocumentsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteDocuments = useInfiniteQuery<
    ConsentsDocumentsResponse,
    Error,
    InfiniteData<ConsentsDocumentsResponse>,
    ['consents-documents', 'infinite', Omit<ConsentsDocumentsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['consents-documents', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return consentsApi.getDocuments({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = (newFilter: Partial<ConsentsDocumentsFilter>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
    }));
  };

  const createDocumentMutation = useMutation({
    mutationFn: consentsApi.createDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['consents-documents', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Документ согласия успешно создан',
        name: 'createConsentDocument',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteDocuments,
    filters,
    updateFilter,
    createDocumentMutation,
  };
};
